import { i18n } from '@lingui/core';
import { DEFAULT_LOCALE } from './i18n.js';

// [i18n wave 2] Locale-aware formatting — spec 4234329d.
//
// Named format.ts, not locale.ts or locale-options.ts: locale-options.ts
// already exists and holds the MARKET picker's country/language tables,
// which is a different concern entirely (where a brand advertises, not
// what its founder reads).
//
// Why a module instead of leaving toLocale* calls in place: the dashboard
// currently does three different things. 14 call sites hardcode 'en-US'
// (always English), 19 pass `undefined` and 65 pass nothing — both of
// which use the BROWSER's locale. So a Spanish-browser founder already
// sees Spanish dates inside English chrome, and a founder who sets
// Spanish explicitly still gets English dates from the hardcoded sites.
// The inconsistency is the bug; routing everything through here fixes it
// by construction.

/** The locale to format in: whatever Lingui currently has active, which
 *  is the resolved interface locale. Falls back rather than throwing —
 *  formatting a date is never the right place to crash a page. */
function activeLocale(): string {
  return i18n.locale || DEFAULT_LOCALE;
}

// Intl constructors are genuinely expensive — enough to show up in a
// table rendering thousands of cells — and are documented as reusable.
// Cache per (locale, options) rather than per call site.
const dateTimeCache = new Map<string, Intl.DateTimeFormat>();
const numberCache = new Map<string, Intl.NumberFormat>();
const relativeCache = new Map<string, Intl.RelativeTimeFormat>();

function dateTimeFormatter(locale: string, options: Intl.DateTimeFormatOptions) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = dateTimeCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    dateTimeCache.set(key, f);
  }
  return f;
}

function numberFormatter(locale: string, options: Intl.NumberFormatOptions) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = numberCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, options);
    numberCache.set(key, f);
  }
  return f;
}

function relativeFormatter(locale: string, options: Intl.RelativeTimeFormatOptions) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = relativeCache.get(key);
  if (!f) {
    f = new Intl.RelativeTimeFormat(locale, options);
    relativeCache.set(key, f);
  }
  return f;
}

/** Intl option keys that decide WHICH PARTS are shown, as opposed to
 *  modifiers like timeZone or hour12 that adjust how the chosen parts
 *  render.
 *
 *  The distinction matters because a caller passing only `timeZone` wants
 *  the default shape rendered in that zone — not Intl's bare numeric
 *  fallback. Treating any option at all as "caller picked the shape" makes
 *  formatDate(d, { timeZone: 'UTC' }) silently return "3/5/2026" instead
 *  of "Mar 5, 2026", which is how a whole surface loses its month names
 *  from one innocuous argument. */
const COMPONENT_KEYS = [
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'dayPeriod',
  'hour',
  'minute',
  'second',
  'fractionalSecondDigits',
  'dateStyle',
  'timeStyle',
] as const satisfies readonly (keyof Intl.DateTimeFormatOptions)[];

function withDefaults(
  given: Intl.DateTimeFormatOptions,
  defaults: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions {
  const callerPickedShape = COMPONENT_KEYS.some((k) => given[k] !== undefined);
  return callerPickedShape ? given : { ...defaults, ...given };
}

/** Invalid dates must never throw out of a formatter.
 *
 *  This is the one real behavioural trap in moving off toLocale*: those
 *  methods return the string "Invalid Date" for an unparseable timestamp,
 *  while Intl.DateTimeFormat.prototype.format throws a RangeError. Call
 *  sites that had been quietly rendering "Invalid Date" for years would
 *  start taking down the surrounding list instead — a grouped library
 *  view, a billing ledger, a notification feed.
 *
 *  Empty string rather than the old "Invalid Date": nothing depends on
 *  that literal, it is a developer string the founder should never read,
 *  and rendering nothing degrades better than rendering a fault. The data
 *  problem is still a data problem; a date formatter is not where it
 *  should surface. */
function invalid(value: Date | number | string): boolean {
  return Number.isNaN(new Date(value).getTime());
}

/** Every helper takes an optional explicit locale. Components should omit
 *  it and inherit the active one; the parameter exists for tests and for
 *  the rare surface that must render a fixed locale. */
export interface FormatOptions {
  locale?: string;
}

export function formatDate(
  value: Date | number | string,
  options: Intl.DateTimeFormatOptions & FormatOptions = {},
): string {
  if (invalid(value)) return '';
  const { locale, ...rest } = options;
  const opts = withDefaults(rest, { year: 'numeric', month: 'short', day: 'numeric' });
  return dateTimeFormatter(locale ?? activeLocale(), opts).format(new Date(value));
}

export function formatTime(
  value: Date | number | string,
  options: Intl.DateTimeFormatOptions & FormatOptions = {},
): string {
  if (invalid(value)) return '';
  const { locale, ...rest } = options;
  const opts = withDefaults(rest, { hour: 'numeric', minute: '2-digit' });
  return dateTimeFormatter(locale ?? activeLocale(), opts).format(new Date(value));
}

export function formatDateTime(
  value: Date | number | string,
  options: Intl.DateTimeFormatOptions & FormatOptions = {},
): string {
  if (invalid(value)) return '';
  const { locale, ...rest } = options;
  const opts = withDefaults(rest, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return dateTimeFormatter(locale ?? activeLocale(), opts).format(new Date(value));
}

export function formatNumber(
  value: number,
  options: Intl.NumberFormatOptions & FormatOptions = {},
): string {
  const { locale, ...rest } = options;
  return numberFormatter(locale ?? activeLocale(), rest).format(value);
}

export function formatPercent(
  /** A ratio, not a percentage: 0.42 renders as 42%. */
  ratio: number,
  options: Intl.NumberFormatOptions & FormatOptions = {},
): string {
  const { locale, ...rest } = options;
  return numberFormatter(locale ?? activeLocale(), {
    style: 'percent',
    maximumFractionDigits: 1,
    ...rest,
  }).format(ratio);
}

/** A count of DAYS as a localized unit — "7d" in English, "7 d" in Spanish.
 *
 *  Not formatRelativeTime: that takes an instant and renders "7d ago". This is
 *  a duration ("rarely served for 7 days"), which has no direction, and
 *  appending a bare "d" in JSX hard-codes the English narrow unit — the thing
 *  Intl already knows per locale. */
export function formatDayCount(
  days: number,
  options: Intl.NumberFormatOptions & FormatOptions = {},
): string {
  const { locale, ...rest } = options;
  return numberFormatter(locale ?? activeLocale(), {
    style: 'unit',
    unit: 'day',
    unitDisplay: 'narrow',
    ...rest,
  }).format(days);
}

/** Money, with the currency REQUIRED rather than defaulted to USD.
 *
 *  Defaulting would let a call site stay silent about which currency its
 *  number is in, which is exactly how ad spend came to be stored in a
 *  cost_usd column while holding whatever currency the ad account reports
 *  (see the wave 6 notes on microsToMajorUnits). A required argument makes each
 *  caller state the assumption where a reader can check it. */
export function formatCurrency(
  amount: number,
  currency: string,
  options: Intl.NumberFormatOptions & FormatOptions = {},
): string {
  const { locale, ...rest } = options;
  return numberFormatter(locale ?? activeLocale(), {
    style: 'currency',
    currency,
    ...rest,
  }).format(amount);
}

/** Money a PLATFORM will charge, formatted in the currency it will
 *  actually be charged in.
 *
 *  Distinct from formatCurrency, which requires a currency and will
 *  happily render soles with a dollar sign if you hand it 'USD'. Ad money
 *  is the case where we often do NOT know the currency — an account
 *  connected before we started reading it — and the honest rendering there
 *  is the number with no symbol at all.
 *
 *  Guessing USD is what produced "$50/day" for a founder whose account
 *  bills in PEN and whose real delivery was about $13.50. A bare "50" is
 *  less informative and far less wrong. */
export function formatAdMoney(
  amount: number,
  currency: string | null,
  options: Intl.NumberFormatOptions & FormatOptions = {},
): string {
  if (!currency) return formatNumber(amount, options);
  return formatCurrency(amount, currency, options);
}

const RELATIVE_THRESHOLDS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
  { unit: 'second', ms: 1000 },
];

/** "3 days ago" / "hace 3 días". Past is negative, future positive.
 *
 *  Picks the largest unit that the difference fills, so a 40-hour gap
 *  reads as a day rather than 40 hours. Anything under a second is "now"
 *  rather than "in 0 seconds", which is what the raw formatter emits. */
export function formatRelativeTime(
  value: Date | number | string,
  options: Intl.RelativeTimeFormatOptions & FormatOptions & { now?: Date | number } = {},
): string {
  const { locale, now, ...rest } = options;
  if (invalid(value)) return '';
  const target = new Date(value).getTime();
  const base = now === undefined ? Date.now() : new Date(now).getTime();
  const diff = target - base;
  const formatter = relativeFormatter(locale ?? activeLocale(), { numeric: 'auto', ...rest });

  for (const { unit, ms } of RELATIVE_THRESHOLDS) {
    if (Math.abs(diff) >= ms) {
      return formatter.format(Math.trunc(diff / ms), unit);
    }
  }
  return formatter.format(0, 'second');
}

/** Compact relative time — "3m ago", "2h ago", "5d ago", "in 2h".
 *
 *  Separate from formatRelativeTime because the dashboard's chips are
 *  tight and the prose form ("3 minutes ago") does not fit them.
 *
 *  style 'narrow' + numeric 'always' was chosen by comparing output
 *  against the hand-rolled English formatters this replaces: it
 *  reproduces them character for character in every case, so adopting it
 *  changes nothing visually in English while giving correct Spanish
 *  ("hace 3 min", "dentro de 2 h"). numeric 'auto' was rejected only
 *  because it renders -1 day as "yesterday" where the existing UI says
 *  "1d ago"; that is a nicer string but it is a visible change, and this
 *  migration is worth keeping invisible.
 *
 *  One deliberate behaviour change: the formatters being replaced used
 *  three different day/hour thresholds (one showed hours up to 47h,
 *  others switched at 24h) — inside the same file, in two cases. This
 *  uses the largest unit that fits, so the 24-47h window now reads
 *  "1d ago" rather than "30h ago". Consistency is the point of
 *  consolidating; preserving three contradictory thresholds is not. */
export function formatRelativeTimeCompact(
  value: Date | number | string,
  options: FormatOptions & {
    now?: Date | number;
    /** Gaps smaller than this collapse to a localized "now" instead of
     *  counting seconds. Defaults to a minute, matching the formatters
     *  this replaces — they returned "just now" below 60s so a chip
     *  wouldn't churn once a second. The string itself is now localized
     *  ("now" / "ahora") rather than hard-coded English. */
    nowThresholdMs?: number;
  } = {},
): string {
  const { nowThresholdMs = 60_000, now, locale } = options;
  if (invalid(value)) return '';
  const target = new Date(value).getTime();
  const base = now === undefined ? Date.now() : new Date(now).getTime();
  if (Math.abs(target - base) < nowThresholdMs) {
    // numeric 'auto' is what turns a zero delta into "now" rather than
    // "in 0 sec."
    return relativeFormatter(locale ?? activeLocale(), {
      numeric: 'auto',
      style: 'narrow',
    }).format(0, 'second');
  }
  return formatRelativeTime(value, {
    ...options,
    style: 'narrow',
    numeric: 'always',
  });
}

/** The localized word for a day offset — "today"/"tomorrow"/"yesterday",
 *  "hoy"/"mañana"/"ayer".
 *
 *  Intl.RelativeTimeFormat with numeric 'auto' produces these directly,
 *  which is why they never need to become catalog entries. A day offset
 *  it has no word for falls back to the numeric form ("in 3 days"). */
export function relativeDayWord(dayOffset: number, options: FormatOptions = {}): string {
  return relativeFormatter(options.locale ?? activeLocale(), { numeric: 'auto' }).format(
    dayOffset,
    'day',
  );
}
